/**
 * Demo-mode in-memory store — the mutable working set behind every service
 * when PLATFORM_MODE is off. Applies the same filter/sort/paging DSL the
 * server would, so the pages behave identically in both modes (and Playwright
 * can drive the full journey without auth).
 */

import type {
  ApiAuditEntry,
  ApiCredential,
  ApiVisit,
  ApiVisitor,
  FilterGroup,
  FilterRule,
  SortSpec,
} from '../services/api-types';
import type { ReceptionExtras } from '../visits/visit-row';
import {
  DEMO_AUDIT,
  DEMO_RECEPTION,
  DEMO_TAG_LIBRARY,
  DEMO_VISITS,
  DEMO_VISITORS,
} from './demo-data';
import { GENERATED } from './demo-generated';
import { DEMO_CREDENTIALS } from './demo-records';
import { assignDemoSpaces } from './demo-spaces';

// Mutable copies — demo mutations write here. The hand-written cast first,
// then the 100 generated visits (`demo-generated.ts`).
export const demoVisits: ApiVisit[] = [...DEMO_VISITS, ...GENERATED.visits].map((v) => ({ ...v }));
export const demoVisitors: ApiVisitor[] = [...DEMO_VISITORS, ...GENERATED.visitors].map((v) => ({
  ...v,
}));

/**
 * Last visit and visit count (the address book's columns; Needs API: only
 * `frontDeskSearch` serves them). The sample cast carries its own; everyone
 * else's come from their visits: how many, and the latest day up to today.
 */
function fillVisitHistory(people: ApiVisitor[], visits: readonly ApiVisit[]): void {
  const pad = (n: number) => String(n).padStart(2, '0');
  const ymd = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const today = ymd(new Date());
  for (const person of people) {
    if (person.visitCount !== undefined) continue;
    const theirs = visits.filter((v) => v.visitorId === person._id && v.expectedDate);
    const past = theirs
      .map((v) => ymd(new Date(v.expectedDate!)))
      .filter((day) => day <= today)
      .sort();
    person.visitCount = theirs.length;
    person.lastVisitOn = past.at(-1) ?? null;
  }
}
fillVisitHistory(demoVisitors, demoVisits);

/** Each person's documents (`credentialsFor`); Delete revokes from here. */
export const demoCredentials: Record<string, ApiCredential[]> = Object.fromEntries(
  Object.entries(DEMO_CREDENTIALS).map(([id, list]) => [id, list.map((c) => ({ ...c }))]),
);

export const demoAudit: Record<string, ApiAuditEntry[]> = { ...GENERATED.audit, ...DEMO_AUDIT };
/** What reception reads beyond the visit grid; the smart cells change it in demo mode. */
export const demoReception: Record<string, ReceptionExtras> = structuredClone({
  ...GENERATED.reception,
  ...(DEMO_RECEPTION as Record<string, ReceptionExtras>),
});
// Their desks and bays, for the Desk and Parking cells and the panel alike.
assignDemoSpaces(demoVisits, demoReception);

/** Each location's tag library; a tag created from the picker joins it. */
export const demoTagLibrary: Record<string, string[]> = Object.fromEntries(
  Object.entries(DEMO_TAG_LIBRARY).map(([id, tags]) => [id, [...tags]]),
);

let seq = 1;
export function demoId(prefix: string): string {
  return `${prefix}-demo-${seq++}`;
}

function get(obj: unknown, path: string): unknown {
  let cur: unknown = obj;
  for (const part of path.split('.')) {
    if (cur == null || typeof cur !== 'object') return undefined;
    cur = (cur as Record<string, unknown>)[part];
  }
  return cur;
}

function asString(v: unknown): string {
  return v == null ? '' : String(v);
}

function ruleMatches(row: unknown, rule: FilterRule): boolean {
  // The grid displays visitor-type NAMES; mirror the server's special case.
  const field = rule.field === 'visitorType' ? 'visitorType' : rule.field;
  const raw = get(row, field);
  const value = asString(rule.value);
  const values = rule.values ?? [];
  switch (rule.operator) {
    case 'EQ':
      return asString(raw).toLowerCase() === value.toLowerCase();
    case 'NEQ':
      return asString(raw).toLowerCase() !== value.toLowerCase();
    case 'IN':
      return Array.isArray(raw)
        ? raw.some((r) => values.some((v) => v.toLowerCase() === asString(r).toLowerCase()))
        : values.some((v) => v.toLowerCase() === asString(raw).toLowerCase());
    case 'NIN':
      return !ruleMatches(row, { ...rule, operator: 'IN' });
    case 'CONTAINS':
      return Array.isArray(raw)
        ? raw.some((r) => asString(r).toLowerCase().includes(value.toLowerCase()))
        : asString(raw).toLowerCase().includes(value.toLowerCase());
    case 'NOT_CONTAINS':
      return !ruleMatches(row, { ...rule, operator: 'CONTAINS' });
    case 'GT':
      return asString(raw) > value;
    case 'LT':
      return asString(raw) < value && raw != null;
    case 'BETWEEN':
      return (
        asString(raw) >= asString(rule.valueRange?.from) &&
        asString(raw) <= asString(rule.valueRange?.to)
      );
    case 'IS_NULL':
      return raw == null || raw === '';
    default:
      return true;
  }
}

export function groupMatches(row: unknown, group: FilterGroup | undefined): boolean {
  if (!group) return true;
  const results = [
    ...(group.rules ?? []).map((r) => ruleMatches(row, r)),
    ...(group.groups ?? []).map((g) => groupMatches(row, g)),
  ];
  if (!results.length) return true;
  return group.op === 'OR' ? results.some(Boolean) : results.every(Boolean);
}

/** Sorts by each spec in turn, as the server does (first name, then last name). */
export function applySort<T>(rows: T[], sort: SortSpec[] | undefined): T[] {
  if (!sort?.length) return rows;
  return [...rows].sort((a, b) => {
    for (const { field, order } of sort) {
      const dir = order === 'ASC' ? 1 : -1;
      const av = asString(get(a, field)).toLowerCase();
      const bv = asString(get(b, field)).toLowerCase();
      if (av !== bv) return av < bv ? -dir : dir;
    }
    return 0;
  });
}

export function searchMatches(row: unknown, term: string, paths: string[]): boolean {
  const t = term.trim().toLowerCase();
  if (!t) return true;
  return paths.some((p) => {
    const v = get(row, p);
    return Array.isArray(v)
      ? v.some((x) => asString(x).toLowerCase().includes(t))
      : asString(v).toLowerCase().includes(t);
  });
}

export function page<T>(rows: T[], skip: number, take: number): { rows: T[]; total: number } {
  return { rows: rows.slice(skip, skip + take), total: rows.length };
}

/** Small latency so loading states render honestly in demo mode. */
export function demoDelay(): Promise<void> {
  return new Promise((r) => setTimeout(r, 120));
}
